'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Search,
  Plus,
  Upload,
  Download,
  AlertCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  SlidersHorizontal,
  Package,
  Minus,
  Image,
  X,
  Pencil,
  Copy,
  Trash2,
} from 'lucide-react'
import { useRecipes, useRecipeBuilderNavigation } from '../context/RecipeBuilderContext'
import { RecipeDetailView } from './RecipeDetailView'
import { RecipeBuilderView } from './RecipeBuilderView'
import type { Recipe, RecipeInventory } from '../types'

/**
 * Recipes View
 *
 * Displays recipe list with server-side search, filtering, and pagination.
 * Each page fetches only the recipes needed from the API.
 */

const RECIPES_PER_PAGE = 10

// localStorage keys for company branding
const COMPANY_LOGO_KEY = 'recipe-builder-company-logo'
const COMPANY_NAME_KEY = 'recipe-builder-company-name'
const COMPANY_WEBSITE_KEY = 'recipe-builder-company-website'

interface PaginationMeta {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export function RecipesView() {
  const { createRecipe, updateRecipe, refreshStats } = useRecipes()
  const { navigateTo, currentView, selectedRecipeId, goBack } = useRecipeBuilderNavigation()

  // Server-side paginated recipes
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: RECIPES_PER_PAGE,
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  // Filters and search
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [categories, setCategories] = useState<string[]>([])

  // Local state for create/edit mode
  const [isBuilderOpen, setIsBuilderOpen] = useState(false)
  const [editingRecipeId, setEditingRecipeId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Stock update modal state
  const [stockModalOpen, setStockModalOpen] = useState(false)
  const [stockRecipe, setStockRecipe] = useState<Recipe | null>(null)
  const [stockQuantity, setStockQuantity] = useState(0)
  const [stockUnit, setStockUnit] = useState('portions')
  const [stockAdjustment, setStockAdjustment] = useState(0)
  const [isUpdatingStock, setIsUpdatingStock] = useState(false)

  // Company branding state
  const [companyLogo, setCompanyLogo] = useState<string | null>(null)
  const [companyName, setCompanyName] = useState<string>("BEN'S FARMHOUSE")
  const [companyWebsite, setCompanyWebsite] = useState<string>("www.bensfarmhouse.com")
  const [logoModalOpen, setLogoModalOpen] = useState(false)

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDuplicating, setIsDuplicating] = useState(false)

  // Debounce search input
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1)
    }, 300)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [searchQuery])

  // Fetch recipes from API with current filters and page
  const fetchRecipes = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(RECIPES_PER_PAGE),
      })
      if (debouncedSearch) params.set('search', debouncedSearch)
      if (statusFilter !== 'all') params.set('status', statusFilter)
      if (categoryFilter !== 'all') params.set('category', categoryFilter)

      const response = await fetch(`/api/recipe-builder/recipes?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch recipes')
      }

      const result = await response.json()
      setRecipes(result.data || [])
      if (result.pagination) {
        setPagination(result.pagination)
      }
    } catch (err) {
      console.error('Failed to fetch recipes:', err)
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }, [currentPage, debouncedSearch, statusFilter, categoryFilter])

  // Fetch categories for filter dropdown
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/recipe-builder/categories')
      if (response.ok) {
        const { data } = await response.json()
        setCategories(data || [])
      }
    } catch {
      // Silently fail - categories dropdown just won't be populated
    }
  }, [])

  // Fetch recipes when filters/page change
  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  // Load company branding from localStorage on mount
  useEffect(() => {
    const savedLogo = localStorage.getItem(COMPANY_LOGO_KEY)
    const savedName = localStorage.getItem(COMPANY_NAME_KEY)
    const savedWebsite = localStorage.getItem(COMPANY_WEBSITE_KEY)
    if (savedLogo) setCompanyLogo(savedLogo)
    if (savedName) setCompanyName(savedName)
    if (savedWebsite) setCompanyWebsite(savedWebsite)
  }, [])

  // Handle logo upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string
        setCompanyLogo(dataUrl)
        localStorage.setItem(COMPANY_LOGO_KEY, dataUrl)
        setLogoModalOpen(false)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle logo removal
  const handleRemoveLogo = () => {
    setCompanyLogo(null)
    localStorage.removeItem(COMPANY_LOGO_KEY)
    setLogoModalOpen(false)
  }

  // Handle create new recipe
  const handleCreateRecipe = () => {
    setEditingRecipeId(null)
    setIsBuilderOpen(true)
  }

  // Handle edit recipe
  const handleEditRecipe = (recipeId: string) => {
    setEditingRecipeId(recipeId)
    setIsBuilderOpen(true)
  }

  // Handle close builder
  const handleCloseBuilder = () => {
    setIsBuilderOpen(false)
    setEditingRecipeId(null)
  }

  // Handle save recipe
  const handleSaveRecipe = async (recipe: Partial<Recipe>) => {
    setIsSaving(true)
    try {
      if (editingRecipeId) {
        // Update existing recipe
        await updateRecipe(editingRecipeId, recipe)
      } else {
        // Create new recipe
        await createRecipe(recipe)
      }
      // Close builder after successful save
      handleCloseBuilder()
      // Refresh the list to show updated data
      fetchRecipes()
      fetchCategories()
    } catch (error) {
      console.error('Failed to save recipe:', error)
      // Show error to user so they know the save failed
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      alert(`Failed to save recipe: ${errorMessage}\n\nPlease try again. If the problem persists, check your internet connection or contact support.`)
      // Don't close the builder - let user retry
    } finally {
      setIsSaving(false)
    }
  }

  // Handle open stock modal
  const handleOpenStockModal = (recipe: Recipe, e: React.MouseEvent) => {
    e.stopPropagation()
    setStockRecipe(recipe)
    setStockQuantity(recipe.inventory?.stock_quantity || 0)
    setStockUnit(recipe.inventory?.stock_unit || 'portions')
    setStockAdjustment(0)
    setStockModalOpen(true)
  }

  // Handle stock update
  const handleUpdateStock = async () => {
    if (!stockRecipe) return

    setIsUpdatingStock(true)
    try {
      const newQuantity = stockQuantity + stockAdjustment
      const updatedInventory: RecipeInventory = {
        stock_quantity: Math.max(0, newQuantity),
        stock_unit: stockUnit,
        min_stock_alert: stockRecipe.inventory?.min_stock_alert || null,
        last_stock_update: new Date().toISOString(),
      }

      await updateRecipe(stockRecipe.id, { inventory: updatedInventory })
      setStockModalOpen(false)
      setStockRecipe(null)
      // Refresh list to show updated stock
      fetchRecipes()
    } catch (error) {
      console.error('Failed to update stock:', error)
    } finally {
      setIsUpdatingStock(false)
    }
  }

  // Quick stock adjustment
  const handleQuickAdjust = (amount: number) => {
    setStockAdjustment((prev) => prev + amount)
  }

  // Generate unique ID for recipes
  const generateId = () => `recipe_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Handle duplicate recipe
  const handleDuplicateRecipe = async (recipe: Recipe) => {
    setIsDuplicating(true)
    try {
      // Create a copy with "(Copy)" appended to the name and a new ID
      const duplicatedRecipe: Partial<Recipe> = {
        id: generateId(),
        name: `${recipe.name} (Copy)`,
        category: recipe.category,
        sub_category: recipe.sub_category,
        description: recipe.description,
        ingredients: recipe.ingredients,
        steps: recipe.steps,
        nutrition: recipe.nutrition,
        allergens: recipe.allergens,
        may_contain_allergens: recipe.may_contain_allergens,
        diet_types: recipe.diet_types,
        serving: recipe.serving,
        costs: recipe.costs,
        labels: recipe.labels,
        metadata: recipe.metadata,
        inventory: undefined, // Don't copy inventory
        status: 'DRAFT', // Set as draft
      }
      await createRecipe(duplicatedRecipe)
      // Refresh list to show the new duplicate
      fetchRecipes()
      fetchCategories()
    } catch (error) {
      console.error('Failed to duplicate recipe:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      alert(`Failed to duplicate recipe: ${errorMessage}`)
    } finally {
      setIsDuplicating(false)
    }
  }

  // Handle delete confirmation
  const handleDeleteClick = (recipe: Recipe) => {
    setRecipeToDelete(recipe)
    setDeleteDialogOpen(true)
  }

  // Handle delete recipe
  const handleDeleteRecipe = async () => {
    if (!recipeToDelete) return

    setIsDeleting(true)
    try {
      const response = await fetch(`/api/recipe-builder/recipes?id=${recipeToDelete.id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete recipe')
      }

      // Refresh the list and stats after successful deletion
      fetchRecipes()
      fetchCategories()
      refreshStats()
    } catch (error) {
      console.error('Failed to delete recipe:', error)
      alert('Failed to delete recipe. Please try again.')
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
      setRecipeToDelete(null)
    }
  }

  // Handle filter changes - reset to page 1
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value)
    setCurrentPage(1)
  }

  // Generate page numbers for pagination UI
  const totalPages = pagination.totalPages
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
      }
    }
    return pages
  }

  const handleRowClick = (recipe: Recipe) => {
    navigateTo('recipe-detail', { recipeId: recipe.id })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'default'
      case 'DRAFT':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  // Compute display range
  const startIndex = (currentPage - 1) * RECIPES_PER_PAGE
  const endIndex = startIndex + recipes.length

  // If in recipe builder mode (create or edit)
  if (isBuilderOpen) {
    return (
      <RecipeBuilderView
        recipeId={editingRecipeId}
        onBack={handleCloseBuilder}
        onSave={handleSaveRecipe}
      />
    )
  }

  // If viewing a specific recipe detail
  if (currentView === 'recipe-detail' && selectedRecipeId) {
    return (
      <RecipeDetailView
        recipeId={selectedRecipeId}
        onBack={goBack}
        onEdit={() => handleEditRecipe(selectedRecipeId)}
        companyLogo={companyLogo}
        companyName={companyName}
        companyWebsite={companyWebsite}
      />
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Recipes</h1>
          <p className="text-muted-foreground">
            Manage your recipes and nutrition labels
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setLogoModalOpen(true)}>
            <Image className="w-4 h-4 mr-2" />
            Company Branding
          </Button>
          <Button size="sm" onClick={handleCreateRecipe}>
            <Plus className="w-4 h-4 mr-2" />
            Add Recipe
          </Button>
        </div>
      </div>

      {/* Company Branding Preview */}
      {(companyLogo || companyName || companyWebsite) && (
        <Card className="bg-muted/30">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {companyLogo && (
                <img
                  src={companyLogo}
                  alt="Company Logo"
                  className="h-10 max-w-[100px] object-contain"
                />
              )}
              <div className="flex flex-col">
                <span className="text-sm font-medium">{companyName}</span>
                <span className="text-xs text-muted-foreground">{companyWebsite}</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLogoModalOpen(true)}
            >
              Edit Branding
            </Button>
          </CardContent>
        </Card>
      )}



      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select
              value={statusFilter}
              onValueChange={handleStatusFilterChange}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="PUBLISHED">Published</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={categoryFilter}
              onValueChange={handleCategoryFilterChange}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" size="sm">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Advanced Filters
            </Button>
          </div>

          <div className="mt-3 text-sm text-muted-foreground">
            Showing {pagination.totalItems === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, pagination.totalItems)} of{' '}
            {pagination.totalItems} recipes
          </div>
        </CardContent>
      </Card>

      {/* Recipe Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Sub Category</TableHead>
              <TableHead>Serving Size</TableHead>
              <TableHead>Calories</TableHead>
              <TableHead>Cost</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Updated On</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  Loading recipes...
                </TableCell>
              </TableRow>
            ) : recipes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  <div className="flex flex-col items-center gap-2">
                    <p>No recipes found</p>
                    <p className="text-sm text-muted-foreground">
                      {pagination.totalItems === 0
                        ? 'Recipes will appear here once loaded from the database.'
                        : 'Try adjusting your search or filters.'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              recipes.map((recipe) => (
                <TableRow
                  key={recipe.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(recipe)}
                >
                  <TableCell className="font-medium">{recipe.name}</TableCell>
                  <TableCell>{recipe.category || '--'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {recipe.sub_category || '--'}
                  </TableCell>
                  <TableCell>
                    {recipe.serving?.serving_size_grams || 100} G
                  </TableCell>
                  <TableCell>
                    {recipe.nutrition?.per_serving?.['Energy']
                      ? `${recipe.nutrition.per_serving['Energy'].quantity.toFixed(1)} Kcal`
                      : '--'}
                  </TableCell>
                  <TableCell>{recipe.costs?.total_cost || 0} AED</TableCell>
                  <TableCell>
                    <button
                      className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors cursor-pointer"
                      onClick={(e) => handleOpenStockModal(recipe, e)}
                    >
                      {recipe.inventory ? (
                        <>
                          {recipe.inventory.min_stock_alert &&
                          recipe.inventory.stock_quantity <= recipe.inventory.min_stock_alert ? (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          ) : (
                            <Package className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span
                            className={
                              recipe.inventory.min_stock_alert &&
                              recipe.inventory.stock_quantity <= recipe.inventory.min_stock_alert
                                ? 'text-amber-600 font-medium'
                                : ''
                            }
                          >
                            {recipe.inventory.stock_quantity} {recipe.inventory.stock_unit}
                          </span>
                        </>
                      ) : (
                        <>
                          <Package className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Set stock</span>
                        </>
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(recipe.status)}>
                      {recipe.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(recipe.updated_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditRecipe(recipe.id)
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDuplicateRecipe(recipe)
                          }}
                          disabled={isDuplicating}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          {isDuplicating ? 'Duplicating...' : 'Duplicate'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteClick(recipe)
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {getPageNumbers().map((page, idx) =>
                typeof page === 'number' ? (
                  <Button
                    key={idx}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                ) : (
                  <span key={idx} className="px-2 text-muted-foreground">
                    {page}
                  </span>
                )
              )}
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Stock Update Modal */}
      <Dialog open={stockModalOpen} onOpenChange={setStockModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Update Stock
            </DialogTitle>
            <DialogDescription>
              {stockRecipe?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Stock Display */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="text-sm text-muted-foreground">Current Stock</span>
              <span className="text-lg font-semibold">
                {stockQuantity} {stockUnit}
              </span>
            </div>

            {/* Quick Adjustment Buttons */}
            <div className="space-y-2">
              <Label>Quick Adjust</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(-10)}
                  className="flex-1"
                >
                  -10
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(-5)}
                  className="flex-1"
                >
                  -5
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(-1)}
                  className="flex-1"
                >
                  -1
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(1)}
                  className="flex-1"
                >
                  +1
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(5)}
                  className="flex-1"
                >
                  +5
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdjust(10)}
                  className="flex-1"
                >
                  +10
                </Button>
              </div>
            </div>

            {/* Adjustment Display */}
            {stockAdjustment !== 0 && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                <span className="text-sm">Adjustment</span>
                <span className={`text-lg font-semibold ${stockAdjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stockAdjustment > 0 ? '+' : ''}{stockAdjustment}
                </span>
              </div>
            )}

            {/* New Stock Total */}
            <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg border border-primary/20">
              <span className="text-sm font-medium">New Stock</span>
              <span className="text-xl font-bold">
                {Math.max(0, stockQuantity + stockAdjustment)} {stockUnit}
              </span>
            </div>

            {/* Manual Input */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manual-quantity">Set Quantity</Label>
                <Input
                  id="manual-quantity"
                  type="number"
                  min="0"
                  value={stockQuantity + stockAdjustment}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 0
                    setStockAdjustment(newValue - stockQuantity)
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stock-unit">Unit</Label>
                <Select value={stockUnit} onValueChange={setStockUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portions">Portions</SelectItem>
                    <SelectItem value="units">Units</SelectItem>
                    <SelectItem value="kg">Kilograms</SelectItem>
                    <SelectItem value="g">Grams</SelectItem>
                    <SelectItem value="l">Liters</SelectItem>
                    <SelectItem value="ml">Milliliters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStockModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStock} disabled={isUpdatingStock}>
              {isUpdatingStock ? 'Updating...' : 'Update Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Branding Modal */}
      <Dialog open={logoModalOpen} onOpenChange={setLogoModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Company Branding
            </DialogTitle>
            <DialogDescription>
              Configure your company branding for packaging artwork. This will be used on all recipe packaging labels.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name</Label>
              <Input
                id="company-name"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value)
                  localStorage.setItem(COMPANY_NAME_KEY, e.target.value)
                }}
                placeholder="BEN'S FARMHOUSE"
              />
            </div>

            {/* Company Website */}
            <div className="space-y-2">
              <Label htmlFor="company-website">Website URL (for QR Code)</Label>
              <Input
                id="company-website"
                value={companyWebsite}
                onChange={(e) => {
                  setCompanyWebsite(e.target.value)
                  localStorage.setItem(COMPANY_WEBSITE_KEY, e.target.value)
                }}
                placeholder="www.bensfarmhouse.com"
              />
              <p className="text-xs text-muted-foreground">
                This URL will be encoded in the QR code on packaging
              </p>
            </div>

            {/* Company Logo */}
            <div className="space-y-2">
              <Label>Company Logo (Optional)</Label>
              {companyLogo ? (
                <div className="space-y-3">
                  <div className="border rounded-lg p-4 bg-gray-50 flex items-center justify-center">
                    <img
                      src={companyLogo}
                      alt="Company Logo"
                      className="max-h-[80px] max-w-full object-contain"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      size="sm"
                      onClick={() => document.getElementById('company-logo-upload')?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Replace
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleRemoveLogo}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => document.getElementById('company-logo-upload')?.click()}
                >
                  <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium mb-1">Click to upload logo</p>
                  <p className="text-xs text-muted-foreground">
                    PNG or JPG, recommended 200x100px
                  </p>
                </div>
              )}
            </div>

            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              id="company-logo-upload"
              onChange={handleLogoUpload}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoModalOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete Recipe
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{recipeToDelete?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false)
                setRecipeToDelete(null)
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRecipe}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Recipe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
